(() => {
  const MOBILE = window.matchMedia('(max-width: 749px)');

  function setupGallery(gallery) {
    if (gallery.dataset.emblaLoopController === 'true') return;

    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    const slider = gallery.querySelector('[id^="Slider-Gallery-"]');

    if (!viewer || !slider) return;

    gallery.dataset.emblaLoopController = 'true';

    const prevButton = viewer.querySelector('button[name="previous"]');
    const nextButton = viewer.querySelector('button[name="next"]');

    let viewport = null;
    let embla = null;
    let observer = null;
    let refreshFrame = null;

    let slides = [];
    let lastSelectedIndex = -1;

    function getSlides() {
      return Array.from(slider.children).filter(
        (slide) =>
          slide.id &&
          slide.id.startsWith('Slide-') &&
          slide.clientWidth > 0
      );
    }

    function getActiveIndex(currentSlides = slides) {
      const index = currentSlides.findIndex((slide) =>
        slide.classList.contains('is-active')
      );

      return index >= 0 ? index : 0;
    }

    function syncGallery(dispatchChange = true) {
      if (!embla) return;

      slides = getSlides();

      if (!slides.length) return;

      let index = embla.selectedScrollSnap();

      index = Math.max(
        0,
        Math.min(index, slides.length - 1)
      );

      /*
       * Keep Dawn's internal state synchronized.
       */
      viewer.currentPage = index + 1;
      viewer.totalPages = slides.length;

      viewer.sliderItems =
        slider.querySelectorAll('[id^="Slide-"]');

      viewer.sliderItemsToShow = slides;

      /*
       * Dawn counter.
       */
      if (viewer.currentPageElement) {
        viewer.currentPageElement.textContent =
          index + 1;
      }

      if (viewer.pageTotalElement) {
        viewer.pageTotalElement.textContent =
          slides.length;
      }

      /*
       * Infinite slider: arrows must always stay enabled.
       */
      prevButton?.removeAttribute('disabled');
      nextButton?.removeAttribute('disabled');

      /*
       * Let Dawn media-gallery.js know which
       * REAL media slide is active.
       *
       * This keeps thumbnails synchronized.
       */
      if (
        dispatchChange &&
        index !== lastSelectedIndex
      ) {
        lastSelectedIndex = index;

        viewer.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: {
              currentPage: index + 1,
              currentElement: slides[index],
            },
          })
        );
      }
    }

    /*
     * Stop Dawn's native previous button handler
     * and let Embla handle it.
     */
    function onPrevious(event) {
      if (!embla || !MOBILE.matches) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      embla.scrollPrev();
    }

    /*
     * Stop Dawn's native next button handler
     * and let Embla handle it.
     */
    function onNext(event) {
      if (!embla || !MOBILE.matches) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      embla.scrollNext();
    }

    /*
     * Dawn can reorder media when a variant changes.
     *
     * Re-read the existing REAL slides and tell
     * Embla about their new order.
     */
    function scheduleRefresh() {
      if (!embla) return;

      cancelAnimationFrame(refreshFrame);

      refreshFrame = requestAnimationFrame(() => {
        if (!embla) return;

        const newSlides = getSlides();

        if (newSlides.length < 2) return;

        const structureChanged =
          newSlides.length !== slides.length ||
          newSlides.some(
            (slide, index) =>
              slide !== slides[index]
          );

        const activeIndex =
          getActiveIndex(newSlides);

        if (structureChanged) {
          slides = newSlides;

          embla.reInit({
            container: slider,
            slides: slides,
            loop: true,
          });
        }

        /*
         * Variant media selected by Dawn?
         * Move Embla to that media instantly.
         */
        if (
          activeIndex >= 0 &&
          activeIndex !==
            embla.selectedScrollSnap()
        ) {
          embla.scrollTo(
            activeIndex,
            true
          );
        }

        syncGallery(true);
      });
    }

    function mount() {
      if (embla) return;
      if (!MOBILE.matches) return;

      if (
        typeof window.EmblaCarousel !==
        'function'
      ) {
        console.error(
          'Product gallery loop: Embla Carousel is not loaded.'
        );

        return;
      }

      slides = getSlides();

      if (slides.length < 2) return;

      /*
       * Create a proper Embla viewport.
       *
       * IMPORTANT:
       * We MOVE Dawn's original UL into this wrapper.
       * Slides are NOT cloned.
       */
      viewport =
        document.createElement('div');

      viewport.className =
        'dawn-product-loop-viewport';

      slider.parentNode.insertBefore(
        viewport,
        slider
      );

      viewport.appendChild(slider);

      slider.classList.add(
        'dawn-product-loop-container'
      );

      /*
       * Dawn should never disable navigation.
       */
      viewer.enableSliderLooping = true;

      prevButton?.removeAttribute('disabled');
      nextButton?.removeAttribute('disabled');

      /*
       * Remove any old native scroll position.
       */
      slider.scrollLeft = 0;

      const startIndex =
        getActiveIndex(slides);

      const direction =
        document.documentElement.dir === 'rtl'
          ? 'rtl'
          : 'ltr';

      embla = window.EmblaCarousel(
        viewport,
        {
          container: slider,
          slides: slides,

          loop: true,
          align: 'start',

          slidesToScroll: 1,

          /*
           * Keep one-image-per-swipe snapping.
           */
          dragFree: false,
          skipSnaps: false,

          /*
           * Smooth carousel physics.
           * This is NOT milliseconds.
           */
          duration: 25,

          startIndex: startIndex,

          direction: direction,

          /*
           * We handle Dawn's media reordering ourselves.
           */
          watchSlides: false,

          watchResize: true,

          /*
           * Don't let focus inside the zoom/modal
           * automatically change carousel position.
           */
          watchFocus: false,

          containScroll: false,
        }
      );

      /*
       * Capture phase is intentional.
       *
       * Dawn already attached its own button
       * click listeners earlier.
       */
      prevButton?.addEventListener(
        'click',
        onPrevious,
        true
      );

      nextButton?.addEventListener(
        'click',
        onNext,
        true
      );

      /*
       * Embla event:
       * update counter + Dawn thumbnails.
       */
      embla.on('select', () => {
        syncGallery(true);
      });

      embla.on('reInit', () => {
        syncGallery(false);
      });

      /*
       * Watch:
       * - variant media reorder
       * - is-active changes
       */
      observer = new MutationObserver(
        scheduleRefresh
      );

      observer.observe(slider, {
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });

      lastSelectedIndex = -1;

      syncGallery(true);
    }

    function unmount() {
      if (!embla) return;

      cancelAnimationFrame(refreshFrame);

      const selectedIndex =
        embla.selectedScrollSnap();

      observer?.disconnect();
      observer = null;

      prevButton?.removeEventListener(
        'click',
        onPrevious,
        true
      );

      nextButton?.removeEventListener(
        'click',
        onNext,
        true
      );

      /*
       * Embla removes its transforms/listeners.
       */
      embla.destroy();
      embla = null;

      slider.classList.remove(
        'dawn-product-loop-container'
      );

      /*
       * Restore Dawn's original DOM structure.
       */
      if (
        viewport &&
        viewport.parentNode
      ) {
        viewport.parentNode.insertBefore(
          slider,
          viewport
        );

        viewport.remove();
      }

      viewport = null;

      viewer.enableSliderLooping = false;

      /*
       * Restore Dawn's own slider calculations
       * for tablet/desktop.
       */
      if (
        typeof viewer.resetPages ===
        'function'
      ) {
        viewer.resetPages();
      }

      const currentSlides = getSlides();

      const currentSlide =
        currentSlides[selectedIndex];

      if (currentSlide) {
        slider.scrollTo({
          left: currentSlide.offsetLeft,
          behavior: 'auto',
        });
      }
    }

    function breakpointChanged(event) {
      if (event.matches) {
        mount();
      } else {
        unmount();
      }
    }

    if (MOBILE.addEventListener) {
      MOBILE.addEventListener(
        'change',
        breakpointChanged
      );
    } else {
      MOBILE.addListener(
        breakpointChanged
      );
    }

    if (MOBILE.matches) {
      mount();
    }
  }

  function initAll(root = document) {
    root
      .querySelectorAll('media-gallery')
      .forEach(setupGallery);
  }

  customElements
    .whenDefined('media-gallery')
    .then(() => {
      initAll();

      /*
       * Shopify Theme Editor support.
       */
      document.addEventListener(
        'shopify:section:load',
        (event) => {
          initAll(event.target);
        }
      );
    });
})();